'use client';

import { useDebounceValue } from '@/hooks/use-debounce';
import { retrieveSearchProduct } from '@/lib/actions/search/action';
import { cn, formatIDR } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRightIcon, LoaderIcon, SearchIcon, XIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, useRef } from 'react';
import Cookies from 'js-cookie';

export interface ModalSearchProps extends React.HtmlHTMLAttributes<HTMLElement> {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  host: string;
  preview: boolean;
}

export default function ModalSearch({ openModal, setOpenModal, host }: ModalSearchProps) {
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [show, setShow] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const debounceKeyword = useDebounceValue(keyword, 300);
  const customerToken = Cookies.get('accessToken');

  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
      // Focus input when modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [openModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setOpenModal?.(false);
      }
    };

    if (openModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openModal, setOpenModal]);

  const findParams = useMemo(
    () => ({
      pageSize: 5,
      page: 1,
      q: debounceKeyword,
    }),
    [debounceKeyword]
  );

  const { data: dataSearch, isLoading } = useQuery({
    queryKey: ['search-list', findParams],
    queryFn: async () => {
      return await retrieveSearchProduct(host, findParams, customerToken as string);
    },
    enabled: debounceKeyword.length >= 3,
  });

  const handleSearch = () => {
    setKeyword('');
    setShow(false);
    setOpenModal?.(false);
    router.push(`/search?keyword=${debounceKeyword}`);
  };

  const handleProductDetail = (slug: string) => {
    setKeyword('');
    setShow(false);
    setOpenModal?.(false);
    router.push(`/products/${slug}`);
  };

  useEffect(() => {
    if (!openModal) setKeyword('');
    setShow(keyword.length >= 3);
  }, [keyword, openModal]);

  useEffect(() => {
    if (openModal) {
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = 'auto';
    }
  }, [openModal]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[105] flex items-start justify-center bg-black/15',
          openModal ? 'block' : 'hidden'
        )}
      >
        <div
          ref={modalRef}
          className='theme_font padding-header w-full bg-white px-2 py-4 transition-all duration-300'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='theme_layout_page_width mx-auto w-full'>
            <div className='mx-auto flex w-full items-center md:w-1/2'>
              <div className='w-full'>
                <div className='relative'>
                  <input
                    name='keyword'
                    type='text'
                    ref={inputRef}
                    value={keyword}
                    placeholder='Search...'
                    className={clsx(
                      'theme_input h-10 w-full px-3 py-1 pr-10',
                      show && 'rounded-bl-none rounded-br-none'
                    )}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <button
                    type='button'
                    onClick={handleSearch}
                    className='absolute right-2 top-1/2 -translate-y-1/2'
                  >
                    <SearchIcon className='mx-auto w-4' />
                  </button>
                </div>
                {show && (
                  <div className='theme_font absolute left-0 z-[106] mx-auto mt-0.5 w-full rounded-sm rounded-tl-none rounded-tr-none bg-white p-0 shadow-lg md:w-1/2 md:translate-x-1/2'>
                    <button
                      type='button'
                      className='flex w-full max-w-full justify-between overflow-hidden px-4 py-4'
                      onClick={() => {
                        handleSearch();
                      }}
                    >
                      <div className='truncate break-all md:break-words'>
                        Search for &quot;{keyword}&quot;
                      </div>
                      <ArrowRightIcon className='h-4 w-4' />
                    </button>
                    <div className='border-b py-4 '>
                      {isLoading ? (
                        <LoaderIcon className='mx-auto mb-4 h-6 w-6 animate-spin' />
                      ) : (
                        <div className='gap-x-6 px-4 pb-6'>
                          <div className=''>
                            <div className='space-y-2 border-b pb-2 uppercase'>Products</div>
                            {dataSearch?.data.map((product) => (
                              <div
                                className='flex gap-x-2 p-2 hover:cursor-pointer hover:bg-gray-100'
                                key={product.id}
                                onClick={() => handleProductDetail(product.slug ?? '')}
                              >
                                <div className='w-16'>
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={
                                      Array.isArray(product.product_assets) &&
                                      typeof product.product_assets[0] === 'string'
                                        ? product.product_assets[0]
                                        : '/path-to-placeholder-image.png'
                                    }
                                    alt={product.product_name}
                                    className='aspect-square w-16 rounded-sm object-cover'
                                  />
                                </div>
                                <div className='w-full max-w-full flex-col overflow-hidden'>
                                  <div className='line-clamp-1 break-all text-sm font-bold md:break-words'>
                                    {product.product_name}
                                  </div>

                                  <div className='theme_search_behavior_product_price mt-1 text-sm'>
                                    {product.discount_price !== undefined &&
                                    product.discount_price > 0 ? (
                                      <>
                                        <span className='text-slate-500 line-through'>
                                          {formatIDR(product.sell_price ?? 0)}
                                        </span>
                                        <div className='mt-1 '>
                                          {formatIDR(product.discount_price)}
                                        </div>
                                      </>
                                    ) : (
                                      <div className='mt-1 '>
                                        {formatIDR(product.sell_price ?? 0)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type='button'
                className='flex h-11 items-center p-2'
                onClick={() => setOpenModal?.(false)}
              >
                <XIcon className='mt-2 h-5 w-5' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
